import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { z } from "zod";

import { fetchMe, meKey, useMe, type Me } from "@/app/session/me";
import { isApiError } from "@/shared/api/errors";
import { request } from "@/shared/api/http";
import { onSessionExpired, readToken, writeToken } from "@/shared/api/token";

export type SessionState =
  | { status: "loading" }
  /** The server could not be reached while restoring the session; retry() tries again. */
  | { status: "unavailable" }
  | { status: "signed-out"; expired: boolean }
  | { status: "signed-in"; user: Me };

interface SessionContextValue {
  state: SessionState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  retry: () => void;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

const loginSchema = z.looseObject({ token: z.string() });

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => readToken() !== null);
  const [expired, setExpired] = useState(false);
  const me = useMe(hasToken);

  useEffect(
    () =>
      onSessionExpired(() => {
        queryClient.clear();
        setExpired(true);
        setHasToken(false);
      }),
    [queryClient],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { token } = await request("login", {
        method: "POST",
        json: { email, password },
        schema: loginSchema,
      });
      writeToken(token);
      queryClient.removeQueries({ queryKey: meKey });
      await queryClient.query({ queryKey: meKey, queryFn: ({ signal }) => fetchMe(signal) });
      setExpired(false);
      setHasToken(true);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await request("logout", { method: "POST", schema: z.unknown() });
    } catch {
      // The token is dropped locally either way.
    } finally {
      writeToken(null);
      queryClient.clear();
      setExpired(false);
      setHasToken(false);
    }
  }, [queryClient]);

  const { refetch } = me;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const state = useMemo<SessionState>(() => {
    if (!hasToken) return { status: "signed-out", expired };
    if (me.data) return { status: "signed-in", user: me.data };
    if (me.isError) {
      return isApiError(me.error, "UNAUTHENTICATED")
        ? { status: "signed-out", expired: true }
        : { status: "unavailable" };
    }
    return { status: "loading" };
  }, [hasToken, expired, me.data, me.isError, me.error]);

  const value = useMemo(() => ({ state, signIn, signOut, retry }), [state, signIn, signOut, retry]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
